import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  catalogueApi,
  clientsApi,
  pricingAgreementsApi,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,

  DateField,
  ErrorState,
  FormSection,
  LoadingState,
  NumberField,
  Panel,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
  newRowKey,
  patchRow,
  withoutRow,
  FormPage,
  useLoadOnce,
} from '../../components/ui/index.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { optionalText } from '../clients/bilingual.js';

/**
 * Create or edit a pricing agreement.
 *
 * AN AGREED PRICE OF ZERO IS PERMITTED HERE, and that is deliberate: a
 * laboratory does genuinely agree to perform one service at no charge as part
 * of a package. It is exactly distinct from a CATALOGUE base price, where zero
 * is refused — an agreed zero is something two parties signed, a catalogue
 * zero would be a price list nobody wrote (CLAUDE.md section 8).
 *
 * The catalogue's current price is shown beside each agreed price FOR
 * COMPARISON ONLY. Changing the catalogue tomorrow changes nothing here: the
 * agreed price is the agreement's own.
 */

interface LineDraft {
  /** The row's own key — never its position (see components/ui/rows.ts). */
  readonly key: string;
  catalogueItemId: string | null;
  /** In MAJOR units for a person to read. Converted on save. */
  agreedPrice: number | null;
}

const emptyLine = (): LineDraft => ({
  key: newRowKey('agreement-line'),
  catalogueItemId: null,
  agreedPrice: null,
});

export function PricingAgreementFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const isNew = id === undefined;
  const canEdit = can(PERMISSIONS.PRICING_AGREEMENTS_MANAGE);

  const [clientId, setClientId] = useState<string | null>(
    searchParams.get('clientId'),
  );
  const [reference, setReference] = useState('');
  const [title, setTitle] = useState('');
  const [validFrom, setValidFrom] = useState<string | null>(null);
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [withdrawReason, setWithdrawReason] = useState('');

  const agreement = useQuery({
    queryKey: ['pricing-agreement', id],
    queryFn: () => pricingAgreementsApi.get(id as string),
    enabled: !isNew,
  });

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
    enabled: isNew,
  });

  const catalogue = useQuery({
    queryKey: ['catalogue', 'selectable'],
    queryFn: () => catalogueApi.list({ pageSize: 100, includeInactive: false }),
  });

  useLoadOnce(agreement.data, (record) => {
    setClientId(record.clientId);
    setReference(record.reference ?? '');
    setTitle(record.title ?? '');
    setValidFrom(record.validFrom);
    setValidUntil(record.validUntil);
    setNotes(record.notes ?? '');
    setLines(
      record.lines.length > 0
        ? record.lines.map((line) => ({
            key: newRowKey('agreement-line'),
            catalogueItemId: line.catalogueItemId,
            agreedPrice: line.agreedPriceMinor / 100,
          }))
        : [emptyLine()],
    );
  });

  const save = useMutation({
    mutationFn: async () => {
      const shared = {
        reference: optionalText(reference),
        title: optionalText(title),
        ...(validFrom ? { validFrom } : {}),
        validUntil,
        notes: optionalText(notes),
      };

      const saved = isNew
        ? await pricingAgreementsApi.create({ ...shared, clientId })
        : await pricingAgreementsApi.update(id as string, shared);

      const payload = lines
        // A line with no price at all is not an agreement about anything, so
        // it is dropped rather than sent as zero.
        .filter((line) => line.catalogueItemId && line.agreedPrice !== null)
        .map((line) => ({
          catalogueItemId: line.catalogueItemId as string,
          agreedPriceMinor: Math.round((line.agreedPrice as number) * 100),
        }));

      return pricingAgreementsApi.setLines(saved.id, { lines: payload });
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['pricing-agreements'] });
      await queryClient.invalidateQueries({
        queryKey: ['pricing-agreement', saved.id],
      });
      show({ tone: 'success', message: t('pricingAgreements.saved') });
      navigate(`/pricing-agreements/${saved.id}`, { replace: true });
    },
  });

  const withdraw = useMutation({
    mutationFn: () =>
      pricingAgreementsApi.update(id as string, {
        isActive: false,
        reason: withdrawReason.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pricing-agreements'] });
      await queryClient.invalidateQueries({ queryKey: ['pricing-agreement', id] });
      show({ tone: 'success', message: t('pricingAgreements.saved') });
    },
  });


  if (!isNew && agreement.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  if (!isNew && agreement.error) {
    return (
      <ErrorState
        error={agreement.error}
        onRetry={() => void agreement.refetch()}
      />
    );
  }

  const record = agreement.data;

  return (
    <FormPage
      title={
        isNew
          ? t('pricingAgreements.createTitle')
          : t('pricingAgreements.editTitle')
      }
      {...(record ? { subtitle: record.clientName ?? record.clientNumber } : {})}
      breadcrumbs={[
        { label: t('nav.pricingAgreements'), to: '/pricing-agreements' },
        {
          label: isNew
            ? t('pricingAgreements.createTitle')
            : t('pricingAgreements.editTitle'),
        },
      ]}
      onSubmit={() => save.mutate()}
      onCancel={() => navigate('/pricing-agreements')}
      isSubmitting={save.isPending}
      canSubmit={canEdit}
      error={save.error}
      fieldLabels={{ clientId: t('pricingAgreements.client'), validFrom: t('pricingAgreements.validFrom'), validUntil: t('pricingAgreements.validUntil') }}
      notices={
        <>
          {record && !record.isActive ? (
            <div className="mb-stack">
              <Alert
                tone="neutral"
                title={t('pricingAgreements.withdrawn')}
                {...(record.deactivationReason
                  ? { description: record.deactivationReason }
                  : {})}
              />
            </div>
          ) : null}
        </>
      }
      after={
        <>

          {/*
            Withdrawal is a separate act with its own recorded reason. It stops the
            agreement applying to NEW work and changes nothing already quoted under
            it — each of those quotations stores the price it used.
          */}
          {!isNew && canEdit && record?.isActive ? (
            <div className="mt-gutter">
              <Panel
                title={t('pricingAgreements.withdraw')}
                description={t('pricingAgreements.withdrawHint')}
              >
                <div className="grid gap-stack">
                  <TextAreaField
                    label={t('pricingAgreements.withdrawReason')}
                    value={withdrawReason}
                    onChange={setWithdrawReason}
                    rows={2}
                    isRequired
                  />
                  <ActionBar>
                    <ActionButton
                      priority="secondary"
                      isDisabled={withdrawReason.trim().length < 3}
                      isLoading={withdraw.isPending}
                      onPress={() => withdraw.mutate()}
                    >
                      {t('pricingAgreements.withdraw')}
                    </ActionButton>
                  </ActionBar>
                </div>
              </Panel>
            </div>
          ) : null}
        </>
      }
    >
        <FormSection title={t('common.details')}>
          {isNew ? (
            <SelectField
              label={t('pricingAgreements.client')}
              placeholder={t('pricingAgreements.selectClient')}
              value={clientId}
              onChange={setClientId}
              isRequired
              options={(clients.data?.data ?? []).map((client) => ({
                value: client.id,
                label: client.name || client.clientNumber,
                description: client.clientNumber,
              }))}
            />
          ) : null}
          <TextField
            label={t('pricingAgreements.reference')}
            description={t('pricingAgreements.referenceHint')}
            value={reference}
            onChange={setReference}
            isReadOnly={!canEdit}
          />
          <TextField
            label={t('pricingAgreements.agreementTitle')}
            value={title}
            onChange={setTitle}
            textDirection="auto"
            isReadOnly={!canEdit}
          />
          <DateField
            label={t('pricingAgreements.validFrom')}
            value={validFrom}
            onChange={setValidFrom}
            isRequired
          />
          <DateField
            label={t('pricingAgreements.validUntil')}
            description={t('pricingAgreements.validUntilHint')}
            value={validUntil}
            onChange={setValidUntil}
          />
        </FormSection>

        <Panel
          title={t('pricingAgreements.lines')}
          description={t('pricingAgreements.agreedZeroHint')}
          actions={
            canEdit ? (
              <ActionButton
                priority="secondary"
                size="sm"
                onPress={() => setLines((current) => [...current, emptyLine()])}
              >
                {t('pricingAgreements.addLine')}
              </ActionButton>
            ) : null
          }
        >
          <div className="grid gap-gutter">
            {lines.length === 0 ? (
              <p className="text-body text-default-600">{t('common.noLinesYet')}</p>
            ) : null}
            {lines.map((line) => {
              const item = catalogue.data?.data.find(
                (candidate) => candidate.id === line.catalogueItemId,
              );
              return (
                <div
                  key={line.key}
                  className="grid min-w-0 grid-cols-1 gap-stack rounded-medium border border-default-200 p-3 sm:grid-cols-3"
                >
                  <SelectField
                    label={t('quotations.selectService')}
                    value={line.catalogueItemId}
                    onChange={(value) =>
                      setLines((current) => patchRow(current, line.key, { catalogueItemId: value }))
                    }
                    isDisabled={!canEdit}
                    options={(catalogue.data?.data ?? []).map((entry) => ({
                      value: entry.id,
                      label:
                        (locale === 'ar' ? entry.name.ar : entry.name.en) ??
                        entry.code ??
                        '',
                    }))}
                  />
                  <NumberField
                    label={t('pricingAgreements.agreedPrice')}
                    value={line.agreedPrice}
                    onChange={(value) =>
                      setLines((current) => patchRow(current, line.key, { agreedPrice: value }))
                    }
                    step="0.01"
                    {...(record?.currency ? { unit: record.currency } : {})}
                    isReadOnly={!canEdit}
                  />
                  <div className="flex items-end justify-between gap-inline">
                    {/*
                      The catalogue's price TODAY, for comparison only. It has
                      no effect on what this agreement charges.
                    */}
                    <span className="text-caption text-default-600">
                      {item
                        ? item.basePriceMinor === null
                          ? t('catalogue.notPriced')
                          : formatMoney(item.basePriceMinor, {
                              locale,
                              currency: item.currency,
                            })
                        : null}
                    </span>
                    {canEdit ? (
                      <ActionButton
                        priority="tertiary"
                        size="sm"
                        onPress={() =>
                          setLines((current) => withoutRow(current, line.key))
                        }
                      >
                        {t('pricingAgreements.removeLine')}
                      </ActionButton>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <FormSection title={t('pricingAgreements.notes')} columns={1}>
          <TextAreaField
            label={t('pricingAgreements.notes')}
            value={notes}
            onChange={setNotes}
            rows={3}
            isReadOnly={!canEdit}
          />
        </FormSection>
    </FormPage>
  );
}
